import { PlusCircleOutlined } from '@ant-design/icons'
import { AlignLeftOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Dropdown, Menu, Typography } from 'antd'
import { partition } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  FetchType,
  PAGE_RESOURCE_POINTS_ENUM,
  Portfolio,
  PpRelationAuditStatus,
  Project,
  ResourceType,
} from '@/schemas'

import { PortfolioRelations } from './portfolio-relations.component'
import { RelationsCreator } from './relations-creator.component'
import { SpecialProjectRelations } from './special-project-relations.component'
import { getPointPermission } from '.'

export const ParentRelations = ({
  selfId,
  type,
  resource,
}: {
  resource: Project | Portfolio
  selfId: string
  type: ResourceType.PROJECT | ResourceType.PORTFOLIO
}) => {
  const { t } = useTranslation()

  const hasReadParentPortfolioPermission = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.概况_关联父组合,
    resource.permissions
  ).canRead
  const hasReadParentSpecialProjectPermission = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.概况_关联父项目,
    resource.permissions
  ).canRead

  const hasAddParentPortfolioPermission = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.概况_关联父组合,
    resource.permissions
  ).editable

  const hasAddParentSpecialProjectPermission = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.概况_关联父项目,
    resource.permissions
  ).editable

  const { data, refresh: refreshRelation } = useRequest<any[]>(
    {
      url: '/api/relations/getPpRelations',
      params: {
        selfId,
        rootTypes: [ResourceType.PORTFOLIO, ResourceType.CUSTOMER, ResourceType.DEVLOP, ResourceType.PRODUCTION],
        depth: 0,
        fetchType: FetchType.fetchParent,
        // 不需要 cycleId, 获取该项目关联的所以 OKR
      },
    },
    {
      refreshDeps: [selfId],
      manual: !(hasReadParentPortfolioPermission || hasReadParentSpecialProjectPermission),
    }
  )

  const relations = data || []
  const [portfolioRelations, specialProjectRelations] = partition(
    relations,
    (item) => item.parentType === ResourceType.PORTFOLIO
  )

  const canAddRelation = relations.length === 0

  const portfolioDisabledSelfIds: (string | number)[] = (portfolioRelations || []).map(
    ({ parentPortfolio }) => parentPortfolio?.selfId
  )

  const canAddPortfolio = hasAddParentPortfolioPermission && canAddRelation
  const portfolioMenu = canAddPortfolio ? (
    <RelationsCreator
      key='portfolio'
      direction='up'
      disabledSelfIds={portfolioDisabledSelfIds}
      rootSelfId={selfId}
      selfId={selfId}
      type={type}
      targetType={ResourceType.PORTFOLIO}
      onSuccess={refreshRelation}
    >
      <Menu.Item key='portfolio'>{t('add portfolios')}</Menu.Item>
    </RelationsCreator>
  ) : null

  const { data: auditRelationData, refresh: refreshRelationAudit } = useRequest<any[]>(
    {
      url: '/api/relations/getAuditsChildView',
      params: {
        selfId,
        type,
      },
    },
    {
      refreshDeps: [selfId],
      manual: !hasReadParentSpecialProjectPermission,
    }
  )

  const refresh = () => {
    refreshRelation()
    refreshRelationAudit()
  }

  const relationDataWithoutAudit = (specialProjectRelations || []).filter(
    ({ parentSelfId }) =>
      !auditRelationData?.find(
        (a) => a.parentSelfId === parentSelfId && a.auditStatus === PpRelationAuditStatus.审批通过
      )
  )
  const canAddSpecialProjectRelation =
    relationDataWithoutAudit.length === 0 &&
    auditRelationData?.every((a) => a.auditStatus !== PpRelationAuditStatus.审批通过) &&
    canAddRelation

  const specialProjectDisabledSelfIds: (string | number)[] = (specialProjectRelations || [])
    .concat(auditRelationData || [])
    .map(
      ({ parentCustomer: customer, parentProduction: production, parentDevlop: devlop }) =>
        customer?.selfId || production?.selfId || devlop?.selfId
    )

  const canAddSpecialProject = hasAddParentSpecialProjectPermission && !!canAddSpecialProjectRelation
  const specialProjectMenu = canAddSpecialProject ? (
    <>
      <RelationsCreator
        key='customer'
        direction='up'
        disabledSelfIds={specialProjectDisabledSelfIds}
        rootSelfId={selfId}
        selfId={selfId}
        type={type}
        targetType={ResourceType.CUSTOMER}
        onSuccess={refresh}
      >
        <Menu.Item key='customer'>{t('add customers')}</Menu.Item>
      </RelationsCreator>
      <RelationsCreator
        key='production'
        direction='up'
        disabledSelfIds={specialProjectDisabledSelfIds}
        rootSelfId={selfId}
        selfId={selfId}
        type={type}
        targetType={ResourceType.PRODUCTION}
        onSuccess={refresh}
      >
        <Menu.Item key='production'>{t('add productions')}</Menu.Item>
      </RelationsCreator>
      <RelationsCreator
        key='devlop'
        direction='up'
        disabledSelfIds={specialProjectDisabledSelfIds}
        rootSelfId={selfId}
        selfId={selfId}
        type={type}
        targetType={ResourceType.DEVLOP}
        onSuccess={refresh}
      >
        <Menu.Item key='devlop'>{t('add devlops')}</Menu.Item>
      </RelationsCreator>
    </>
  ) : null

  return (
    <div>
      <Typography.Title level={4} className='flex items-center'>
        <AlignLeftOutlined className='mr-8px' />
        <span>{t('associate.parent')}</span>
        {canAddPortfolio || canAddSpecialProject ? (
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                {portfolioMenu}
                {specialProjectMenu}
              </Menu>
            }
          >
            <PlusCircleOutlined className='ml-12px' style={{ fontSize: 14 }} />
          </Dropdown>
        ) : null}
      </Typography.Title>

      <div className='space-y-12px'>
        <SpecialProjectRelations
          selfId={selfId}
          type={type}
          relations={specialProjectRelations}
          canRead={hasReadParentSpecialProjectPermission}
          refresh={refresh}
          refreshRelationAudit={refreshRelationAudit}
          auditRelationData={auditRelationData}
        />
        <PortfolioRelations
          selfId={selfId}
          type={type}
          relations={portfolioRelations}
          canRead={hasReadParentPortfolioPermission}
          refresh={refresh}
        />
      </div>
    </div>
  )
}
